import {
  useCallback,
  useMemo,
  useState,
} from "react";

import {
  CALENDAR_EVENT_PALETTES,
  getEventPaletteKey,
} from "../data/calendarEventColors";

import {
  DISCIPLINE_OPTIONS,
} from "../data/eventOptions";

import {
  AGE_CATEGORIES,
  extractEventCategories,
  getCurrentSeasonLabel,
  getSeasonLabelFromDate,
  normalize,
  sortEventsByStartDate,
  toDateKey,
} from "../utils/calendarUtils";

function isEventActive(event) {
  return (
    event?.is_active === true ||
    event?.is_active === 1 ||
    event?.is_active === "1" ||
    normalize(event?.is_active) === "true"
  );
}

function uniqueValues(values = []) {
  const result = [];
  const normalizedValues = new Set();

  values.forEach((value) => {
    const trimmedValue = String(value || "").trim();

    if (!trimmedValue) return;

    const normalizedValue = normalize(trimmedValue);

    if (normalizedValues.has(normalizedValue)) {
      return;
    }

    normalizedValues.add(normalizedValue);
    result.push(trimmedValue);
  });

  return result;
}

function sortValuesByReference(values = [], reference = []) {
  const referenceMap = new Map(
    reference.map((value, index) => [
      normalize(value),
      index,
    ])
  );

  return [...values].sort((first, second) => {
    const firstIndex =
      referenceMap.get(normalize(first)) ??
      Number.MAX_SAFE_INTEGER;

    const secondIndex =
      referenceMap.get(normalize(second)) ??
      Number.MAX_SAFE_INTEGER;

    if (firstIndex !== secondIndex) {
      return firstIndex - secondIndex;
    }

    return first.localeCompare(second, "fr");
  });
}

function eventMatchesSelectedValues(
  eventValue,
  selectedValues
) {
  if (selectedValues.length === 0) {
    return true;
  }

  const normalizedEventValue = normalize(eventValue);

  return selectedValues.some(
    (selectedValue) =>
      normalize(selectedValue) === normalizedEventValue
  );
}

export default function useCalendarFilters(
  rawEvents = []
) {
  const [search, setSearch] = useState("");

  const [
    selectedDisciplines,
    setSelectedDisciplines,
  ] = useState([]);

  const [selectedAges, setSelectedAges] =
    useState([]);

  const [
    selectedOrganisateurs,
    setSelectedOrganisateurs,
  ] = useState([]);

  const [selectedTypes, setSelectedTypes] =
    useState([]);

  const [selectedLevels, setSelectedLevels] =
    useState([]);

  const [selectedSeason, setSelectedSeason] =
    useState(() => getCurrentSeasonLabel());

  const [startAfter, setStartAfter] =
    useState("");

  const [startBefore, setStartBefore] =
    useState("");

  /*
   * Les couleurs contenues dans ce tableau
   * sont masquées dans le calendrier.
   */
  const [
    hiddenPaletteKeys,
    setHiddenPaletteKeys,
  ] = useState([]);

  const activeEvents = useMemo(() => {
    return rawEvents
      .filter(isEventActive)
      .filter(
        (event) =>
          normalize(event?.titre) !==
          normalize("Cours CN PAAM")
      );
  }, [rawEvents]);

  const allDisciplines = useMemo(() => {
    const values = uniqueValues(
      activeEvents.map(
        (event) => event.discipline_event
      )
    );

    return sortValuesByReference(
      values,
      DISCIPLINE_OPTIONS
    );
  }, [activeEvents]);

  const allOrganisateurs = useMemo(() => {
    return uniqueValues(
      activeEvents.map(
        (event) => event.organisateur
      )
    ).sort((first, second) =>
      first.localeCompare(second, "fr")
    );
  }, [activeEvents]);

  const allTypes = useMemo(() => {
    return uniqueValues(
      activeEvents.map(
        (event) => event.type_event
      )
    ).sort((first, second) =>
      first.localeCompare(second, "fr")
    );
  }, [activeEvents]);

  const allLevels = useMemo(() => {
    return uniqueValues(
      activeEvents.map(
        (event) => event.level_event
      )
    ).sort((first, second) =>
      first.localeCompare(second, "fr")
    );
  }, [activeEvents]);

  const allSeasons = useMemo(() => {
    const currentSeason =
      getCurrentSeasonLabel();

    return Array.from(
      new Set([
        currentSeason,

        ...activeEvents
          .map((event) =>
            getSeasonLabelFromDate(event.start)
          )
          .filter(Boolean),
      ])
    )
      .filter(Boolean)
      .sort()
      .reverse();
  }, [activeEvents]);

  /*
   * Tous les filtres sauf ceux provenant
   * de la légende colorée.
   */
  const legendEvents = useMemo(() => {
    let events = [...activeEvents];

    if (selectedDisciplines.length > 0) {
      events = events.filter((event) =>
        eventMatchesSelectedValues(
          event.discipline_event,
          selectedDisciplines
        )
      );
    }

    if (selectedOrganisateurs.length > 0) {
      events = events.filter((event) =>
        eventMatchesSelectedValues(
          event.organisateur,
          selectedOrganisateurs
        )
      );
    }

    if (selectedTypes.length > 0) {
      events = events.filter((event) =>
        eventMatchesSelectedValues(
          event.type_event,
          selectedTypes
        )
      );
    }

    if (selectedLevels.length > 0) {
      events = events.filter((event) =>
        eventMatchesSelectedValues(
          event.level_event,
          selectedLevels
        )
      );
    }

    if (selectedAges.length > 0) {
      const normalizedSelectedAges = new Set(
        selectedAges.map((value) =>
          normalize(value)
        )
      );

      events = events.filter((event) => {
        const categories =
          extractEventCategories(
            event.cate_event
          );

        return categories.some((category) =>
          normalizedSelectedAges.has(
            normalize(category)
          )
        );
      });
    }

    if (selectedSeason) {
      events = events.filter(
        (event) =>
          getSeasonLabelFromDate(event.start) ===
          selectedSeason
      );
    }

    if (startAfter) {
      events = events.filter((event) => {
        const eventDate = toDateKey(event.start);

        return (
          eventDate &&
          eventDate >= startAfter
        );
      });
    }

    if (startBefore) {
      events = events.filter((event) => {
        const eventDate = toDateKey(event.start);

        return (
          eventDate &&
          eventDate <= startBefore
        );
      });
    }

    if (search.trim()) {
      const query = normalize(search);

      events = events.filter((event) => {
        const searchableText = [
          event.titre,
          event.description,
          event.discipline_event,
          event.lieu_event,
          event.organisateur,
          event.type_event,
          event.level_event,
          event.cate_event,
          event.horaire_event,
        ]
          .map(normalize)
          .join(" ");

        return searchableText.includes(query);
      });
    }

    return events.sort(sortEventsByStartDate);
  }, [
    activeEvents,
    search,
    selectedDisciplines,
    selectedAges,
    selectedOrganisateurs,
    selectedTypes,
    selectedLevels,
    selectedSeason,
    startAfter,
    startBefore,
  ]);

  const filteredEvents = useMemo(() => {
    if (hiddenPaletteKeys.length === 0) {
      return legendEvents;
    }

    const hiddenSet = new Set(
      hiddenPaletteKeys
    );

    return legendEvents.filter(
      (event) =>
        !hiddenSet.has(
          getEventPaletteKey(event)
        )
    );
  }, [legendEvents, hiddenPaletteKeys]);

  const toggleValue = useCallback(
    (value, setter) => {
      setter((currentValues) =>
        currentValues.includes(value)
          ? currentValues.filter(
              (currentValue) =>
                currentValue !== value
            )
          : [...currentValues, value]
      );
    },
    []
  );

  const togglePaletteVisibility =
    useCallback((paletteKey) => {
      setHiddenPaletteKeys((currentKeys) =>
        currentKeys.includes(paletteKey)
          ? currentKeys.filter(
              (currentKey) =>
                currentKey !== paletteKey
            )
          : [...currentKeys, paletteKey]
      );
    }, []);

  const clearFilters = useCallback(() => {
    setSearch("");
    setSelectedDisciplines([]);
    setSelectedAges([]);
    setSelectedOrganisateurs([]);
    setSelectedTypes([]);
    setSelectedLevels([]);
    setSelectedSeason(
      getCurrentSeasonLabel()
    );
    setStartAfter("");
    setStartBefore("");
    setHiddenPaletteKeys([]);
  }, []);

  const activeChips = useMemo(() => {
    const chips = [];

    if (search.trim()) {
      chips.push({
        id: "search",
        type: "search",
        value: search,
        label: `Recherche : ${search}`,
      });
    }

    if (selectedSeason) {
      chips.push({
        id: "season",
        type: "season",
        value: selectedSeason,
        label: `Saison ${selectedSeason}`,
      });
    }

    if (startAfter) {
      chips.push({
        id: "startAfter",
        type: "startAfter",
        value: startAfter,
        label: `À partir du ${new Date(
          `${startAfter}T12:00:00`
        ).toLocaleDateString("fr-FR")}`,
      });
    }

    if (startBefore) {
      chips.push({
        id: "startBefore",
        type: "startBefore",
        value: startBefore,
        label: `Jusqu’au ${new Date(
          `${startBefore}T12:00:00`
        ).toLocaleDateString("fr-FR")}`,
      });
    }

    selectedDisciplines.forEach((value) => {
      chips.push({
        id: `discipline-${value}`,
        type: "discipline",
        value,
        label: `Discipline : ${value}`,
      });
    });

    selectedAges.forEach((value) => {
      chips.push({
        id: `age-${value}`,
        type: "age",
        value,
        label: value,
      });
    });

    selectedOrganisateurs.forEach(
      (value) => {
        chips.push({
          id: `organisateur-${value}`,
          type: "organisateur",
          value,
          label: value,
        });
      }
    );

    selectedTypes.forEach((value) => {
      chips.push({
        id: `type-${value}`,
        type: "type",
        value,
        label: value,
      });
    });

    selectedLevels.forEach((value) => {
      chips.push({
        id: `level-${value}`,
        type: "level",
        value,
        label: value,
      });
    });

    hiddenPaletteKeys.forEach(
      (paletteKey) => {
        const palette =
          CALENDAR_EVENT_PALETTES[
            paletteKey
          ];

        chips.push({
          id: `palette-${paletteKey}`,
          type: "palette",
          value: paletteKey,
          label: `Masqué : ${
            palette?.label || paletteKey
          }`,
        });
      }
    );

    return chips;
  }, [
    search,
    selectedSeason,
    startAfter,
    startBefore,
    selectedDisciplines,
    selectedAges,
    selectedOrganisateurs,
    selectedTypes,
    selectedLevels,
    hiddenPaletteKeys,
  ]);

  const removeFilterChip = useCallback(
    (chip) => {
      switch (chip.type) {
        case "search":
          setSearch("");
          break;

        case "season":
          setSelectedSeason("");
          break;

        case "startAfter":
          setStartAfter("");
          break;

        case "startBefore":
          setStartBefore("");
          break;

        case "discipline":
          setSelectedDisciplines((values) =>
            values.filter(
              (value) =>
                value !== chip.value
            )
          );
          break;

        case "age":
          setSelectedAges((values) =>
            values.filter(
              (value) =>
                value !== chip.value
            )
          );
          break;

        case "organisateur":
          setSelectedOrganisateurs(
            (values) =>
              values.filter(
                (value) =>
                  value !== chip.value
              )
          );
          break;

        case "type":
          setSelectedTypes((values) =>
            values.filter(
              (value) =>
                value !== chip.value
            )
          );
          break;

        case "level":
          setSelectedLevels((values) =>
            values.filter(
              (value) =>
                value !== chip.value
            )
          );
          break;

        case "palette":
          setHiddenPaletteKeys((values) =>
            values.filter(
              (value) =>
                value !== chip.value
            )
          );
          break;

        default:
          break;
      }
    },
    []
  );

  return {
    AGE_CATEGORIES,
    DISCIPLINE_OPTIONS,

    search,
    setSearch,

    selectedDisciplines,
    setSelectedDisciplines,

    selectedAges,
    setSelectedAges,

    selectedOrganisateurs,
    setSelectedOrganisateurs,

    selectedTypes,
    setSelectedTypes,

    selectedLevels,
    setSelectedLevels,

    selectedSeason,
    setSelectedSeason,

    startAfter,
    setStartAfter,

    startBefore,
    setStartBefore,

    hiddenPaletteKeys,
    setHiddenPaletteKeys,

    allDisciplines,
    allOrganisateurs,
    allTypes,
    allLevels,
    allSeasons,

    activeEvents,
    legendEvents,
    filteredEvents,

    activeChips,
    activeFilterCount:
      activeChips.length,

    toggleValue,
    togglePaletteVisibility,

    removeFilterChip,
    clearFilters,
  };
}